/**
 * Normalize an object's properties into title case
 * IE "CUSTOMER_ID" > "Customer_ID"
 * @param o
 * @returns
 */
export const normalizePropsTitleCase = <t>(o: any): t =>
  Object.keys(o).reduce((prev, curr) => {
    const key = curr
      .toLowerCase()
      .split('_')
      .map(s => {
        if (s === 'id') return 'ID';
        return s.charAt(0).toUpperCase() + s.slice(1);
      })
      .join('_');
    return { ...prev, [key]: o[curr] };
  }, {}) as t;

/** Deep clone of plain data via structuredClone. Class prototypes are not preserved. */
export function deepClone<T>(value: T): T {
  return structuredClone(value);
}

/**
 * Structural equality for plain data: primitives (NaN-safe), Date, RegExp,
 * arrays and plain objects. Functions and class instances are compared by reference.
 */
export function deepEqual(a: any, b: any): boolean {
  if (Object.is(a, b)) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;

  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (a instanceof RegExp && b instanceof RegExp) return a.source === b.source && a.flags === b.flags;

  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (aIsArray !== bIsArray) return false;
  if (aIsArray && bIsArray) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }
  return true;
}

/** True for null/undefined, empty string, empty array, or plain object with no own enumerable keys. */
export function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string' || Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value as object).length === 0;
  return false;
}

/** Safe own-property check that ignores prototype chain. */
export function hasOwn(obj: unknown, key: PropertyKey): boolean {
  return obj != null && Object.prototype.hasOwnProperty.call(obj, key);
}
